import envConfig from "../../../utils/env.config";

const BUILD_RESTAURANT_ID_KEY = "prepit_build_restaurant_id";
const PERSIST_KEY_PREFIX = "persist:";

const safeJsonParse = (value: string | null) => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

// When the stamp is missing (state written before this guard shipped) fall
// back to inspecting the persisted config slice: a restaurantInfo.id that
// differs from the build id is stale, unless the build restaurant is in the
// persisted partner list (i.e. the user legitimately switched to a partner
// restaurant within this deployment).
//
// READS TWO KEYS THAT ARE NO LONGER WRITTEN. `restaurantInfo` and
// `partnerRestaurants` are blacklisted from the config persist (see store.tsx),
// so for any state written after that change both come back undefined and this
// returns false -- "not stale" -- which is correct, because such a browser also
// carries the BUILD_RESTAURANT_ID_KEY stamp and never reaches this fallback.
//
// It stays for the population it was written for: browsers whose state predates
// the stamp. Those still hold both keys, because they stored them before the
// blacklist. Delete this and a returning visitor on a re-pointed domain who has
// not loaded the site since the stamp shipped keeps the old restaurant's ids --
// the bug this file exists to fix.
const isLegacyStateStale = (buildRestaurantId: number) => {
  const persistedConfig = safeJsonParse(
    localStorage.getItem(`${PERSIST_KEY_PREFIX}config`)
  );
  if (!persistedConfig) {
    return false;
  }
  const persistedRestaurantId = safeJsonParse(
    persistedConfig.restaurantInfo
  )?.id;
  if (!persistedRestaurantId || persistedRestaurantId === buildRestaurantId) {
    return false;
  }
  const partnerRestaurants: Array<{ id?: number }> =
    safeJsonParse(persistedConfig.partnerRestaurants) ?? [];

  return !partnerRestaurants.some(
    (partner) => partner?.id === buildRestaurantId
  );
};

/**
 * Domains can be re-pointed to a different restaurant across deployments
 * (e.g. maine.prepit.app moving from restaurant 21 to 876). redux-persist
 * would then keep serving the old restaurant/franchise ids from localStorage,
 * breaking every returning visitor. Drop all persisted state when the build's
 * restaurant id no longer matches the one the state was written for.
 *
 * Must run in the browser before persistStore() triggers rehydration.
 */
export const purgeStaleRestaurantPersistedState = () => {
  if (typeof window === "undefined" || !envConfig.restaurantId) {
    return;
  }
  try {
    const stampedBuildId = localStorage.getItem(BUILD_RESTAURANT_ID_KEY);
    const isStale = stampedBuildId
      ? stampedBuildId !== envConfig.restaurantId
      : isLegacyStateStale(parseInt(envConfig.restaurantId));

    if (isStale) {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(PERSIST_KEY_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    }
    localStorage.setItem(BUILD_RESTAURANT_ID_KEY, envConfig.restaurantId);
  } catch {
    // Storage unavailable (private mode quirks, disabled storage) - never
    // block app boot for the purge.
  }
};
